<template>
  <authentic-modal  ref="billingModal" id="billing-modal" :on-submit="submit" :on-cancel="cancel" :size="'lg'"
    :title="modalTitle" :hideFooter="true" :no-close-on-backdrop="true">
    <div id="overlay" :style="showModalOverlay ? 'display:flex' : 'display:none'" class="billing-modal-overlay">
      <div id="text" class="d-flex flex-column align-items-center justify-content-center">
        <div class="row">
          <div class="spinner-border text-primary" role="status">
          </div>
        </div>
        <div class="row">
          <span>{{overlayMessage}}</span>
        </div>
      </div>
    </div>

    <div v-if="!hasPlan && customerInfo !== null" class="container pb-1" style="min-height: 365px; ">
      <div class="row">
        <div class="col-12 col-lg-6 px-0 px-md-4 px-lg-0">
          <div class="row h-100 px-3">
            <div>
              <BillingAddItems v-if="updateQuantity" :billing-details="(billingInformation as Billing)" />
            </div>
          </div>
        </div>
        <div class="col billing-details">
          <BillingDetails v-if="updateQuantity" :subscription-type="'free'" :payment-frequency="'monthly'" :subscription-change="false"   />
        </div>
      </div>
    </div>

    <div v-else-if="customerInfo === null" id="testsize" class="container billing">
      <BillingError />
    </div>

    <div v-else id="testsize" class="container billing" :style="{ height: billingInformation?.subscriptionName === 'Free' ? '315px !important' : '' }">
      <transition name="component-fade" mode="out-in">
        <div>
          <BillingError v-if="billingSummary && !billingInformation?.subscriptionName" />
          <BillingSummary v-if="billingSummary && billingInformation?.subscriptionName !== 'Free'" :billing-details="(billingInformation as Billing)" />
          <BillingUpdatePlan v-if="billingUpdate && billingInformation?.subscriptionName !== 'Free'" :billing-details="(billingInformation as Billing)" />
          <BillingAddPayment v-if="addPayment && billingInformation?.subscriptionName !== 'Free'" />
          <BillingAddItems v-if="updateQuantity" :billing-details="(billingInformation as Billing)"
            :unarchive="unarchive" />
          <BillingCancelPlan v-if="billingCancelPlan && billingInformation?.subscriptionName !== 'Free'" :billing-details="(billingInformation as Billing)" />
        </div>
      </transition>
    </div>
  </authentic-modal>
</template>

<script setup lang="ts">
  import AuthenticModal from "../shared/AuthenticModal.vue";
  import {
    getCustomerStatus,
    setDefaultPayment,
    removePayment,
  } from "../../services/PaymentService";
  import User from "../../models/User";
  import Billing from "../../models/Billing";
  import BillingUpdatePlan from "./BillingUpdatePlan.vue";
  import BillingSummary from "./BillingSummary.vue";
  import BillingAddPayment from "./BillingAddPayment.vue";
  import BillingAddItems from "./BillingAddItems.vue";
  import BillingCancelPlan from "./BillingCancelPlan.vue";
  import BillingDetails from "../shared/BillingDetails.vue";
  import BillingError from "./BillingError.vue";
  import { computed, ref, inject, onBeforeMount, onBeforeUnmount } from "vue";
  import { useStore } from "vuex";
  import { useI18n } from "vue-i18n";

  const billingModal = ref<typeof AuthenticModal>();
  const $store = useStore();
  const { t } = useI18n();
  const emitter: any = inject("emitter");

  const user = computed((): User => {
    return $store.getters.authUser;
  })

  const plan = computed(() => {
    return $store.getters.plans;
  });

  const subscription = computed(() => {
    return $store.getters.subscription;
  });

  const hasPlan = computed(() => {
    if(customerInfo.value) {
      return true;
    }
    return false;
  });

  const showModalOverlay = ref(false);
  const overlayMessage = ref(t("billing_summary.getting_account_information"));

  const billingUpdate = ref(false);
  const billingSummary = ref(true);
  const addPayment = ref(false);
  const updateQuantity = ref(false);
  const billingCancelPlan = ref(false);
  const modalTitle = ref(t("billing_summary.title"));
  const billingInformation = ref<Partial<Billing> | null >({});
  const unarchive = ref(false);
  const isNonPlanPurchase = ref(false);
  const customerInfo = ref<any>(null);

  onBeforeMount(() => {
    $store.dispatch("getPlans");
    emitter.on("billing-modal-close", () => {
      billingModal.value!.hideModal();
    });

    emitter.on("update-plan", () => {
      billingSummary.value = false;
      billingUpdate.value = true;
      addPayment.value = false;
      updateQuantity.value = false;
      billingCancelPlan.value = false;
      
    });

    emitter.on("billing-freeplan-add-item", async () => {
      isNonPlanPurchase.value = true;
      showModalOverlay.value = true;
      billingModal.value!.showModal();
      customerInfo.value = await getCustomerStatus(user.value);
      // console.log("Billing information: ", billingInformation.value);
      if( customerInfo.value ) {
        billingInformation.value = customerInfo.value;
      } else {
        setBillingInformation();
      }
      showModalOverlay.value = false;
      updateQuantity.value = true;
    });

    emitter.on("billing-summary", async (update: string) => {
      if (update === "refresh") {
        emitter.emit("refreshProducts");
        overlayMessage.value = t("billing_summary.getting_updated_account_information");
        showModalOverlay.value = true;
        customerInfo.value = await getCustomerStatus(user.value);
        billingInformation.value = customerInfo.value;
        showModalOverlay.value = false;
      }
      billingSummary.value = true;
      billingUpdate.value = false;
      addPayment.value = false;
      updateQuantity.value = false;
      billingCancelPlan.value = false;
    });

    emitter.on("billing-add-payment", () => {
      billingSummary.value = false;
      billingUpdate.value = false;
      addPayment.value = true;
      updateQuantity.value = false;
      billingCancelPlan.value = false;
    });

    emitter.on(
      "billing-update-quantity",
      async (_updateQuantity: {productList: boolean, fromArchive: boolean}) => {
        billingSummary.value = false;
        billingUpdate.value = false;
        addPayment.value = false;
        billingCancelPlan.value = false;
        if (_updateQuantity.productList) {
          showModalOverlay.value = true;
          billingModal.value!.showModal();
          customerInfo.value = await getCustomerStatus(user.value);
          billingInformation.value = customerInfo.value 
          showModalOverlay.value = false;
        }
        unarchive.value = _updateQuantity.fromArchive;
        updateQuantity.value = true;
      }
    );

    emitter.on("billing-modal", async () => {
      billingSummary.value = true;
      billingUpdate.value = false;
      addPayment.value = false;
      updateQuantity.value = false;
      billingCancelPlan.value = false;

      showModalOverlay.value = true;
      if (billingModal.value) {
        billingModal.value.showModal();
      }
      customerInfo.value = await getCustomerStatus(user.value);
      console.log(customerInfo.value, 'customer info retrieved')
      billingInformation.value = customerInfo.value 
      console.log('billing modal opened', billingInformation.value)
      showModalOverlay.value = false;
    });

    emitter.on("cancel-plan", async () => {
      billingSummary.value = false;
      billingUpdate.value = false;
      addPayment.value = false;
      updateQuantity.value = false;
      billingCancelPlan.value = true;
      billingInformation.value = await getCustomerStatus(user.value);
    });
  });

  function submit() {
    billingModal.value!.hideModal();
  }

  function cancel() {
    billingModal.value!.hideModal();
  }

  async function setBillingInformation() {
    const freePlan = plan.value.find((p: any) => p.name === "free");
    billingInformation.value!.subscriptionName = 'Free';
    billingInformation.value!.paymentInterval = subscription.value.frequency;
    billingInformation.value!.paymentMethods = [];
    billingInformation.value!.billingHistory = [];
    billingInformation.value!.additionalProducts = subscription.value.additionalProducts;
    billingInformation.value!.additionalUsers = subscription.value.additionalUsers;
    billingInformation.value!.additionalCtas = subscription.value.additionalCtas;
    billingInformation.value!.productNumber = subscription.value.productNumber;
    billingInformation.value!.ctaNumber = subscription.value.ctaNumber;
    billingInformation.value!.additionalCtaPrice = freePlan.additionalCtaPrice;
    billingInformation.value!.planPrice = freePlan.price + freePlan.additionalCtaPrice * subscription.value.additionalCtas;
    billingInformation.value!.additionalProductPrice = 0;
    billingInformation.value!.additionalUserPrice = 0;
  }

  onBeforeUnmount(() => {
    emitter.off("billing-management")
    emitter.off("billing-modal");
    emitter.off("billing-modal-close");
    emitter.off("billing-freeplan-add-item");
  });
</script>

<style scoped>
.billing-details {
  padding: 0 0.5rem;
}

.component-fade-enter-active,
.component-fade-leave-active {
  transition: opacity 0.3s ease;
}

.component-fade-enter-from,
.component-fade-leave-to {
  opacity: 0;
}

.billing-modal-overlay{
  height:500px;
}

#overlay {
  position: absolute;
  display: none;
  width: 98%;
  height: 98%;
  top: 0px;
  left: 8px;
  right: 0;
  bottom: 0;
  background-color: rgba(250, 250, 250, 0.85);
  z-index: 3;
  border-radius: 8px;
  align-items: center;
  justify-content: center;
}

:deep .tooltip-inner {
  text-align: left;
}

.payment-methods {
  min-height: 105px;
}

.billing {
  height: 575px !important;
  padding: 0;
  margin: 0;
}

.history-container {
  overflow: auto;
  max-height: 136px;
}

.default-action {
  opacity: 0.5;
}

:deep .set-card-spacing {
  padding-right: 3rem !important;
}

:deep .set-expire-spacing {
  padding-right: 1.5rem !important;
}

:deep .b-table-sticky-header {
  max-height: 125px;
}

:deep .dropdown .btn {
  background-color: transparent !important;
  color: #495057 !important;
  border: none;
}

:deep .table th,
.table td {
  vertical-align: middle !important;
  border-top: none;
  background-color: #fff !important;
}

:deep .dropdown .btn-secondary:focus,
.btn-secondary.focus {
  color: #495057;
  border-color: #545b62;
  box-shadow: none;
}

:deep .btn-secondary:not(:disabled):not(.disabled):active,
.btn-secondary:not(:disabled):not(.disabled).active,
.show>.btn-secondary.dropdown-toggle {
  color: #495057;
  background-color: transparent !important;
  border-color: none;
}

h5 {
  font-family: "proximanova-medium", sans-serif !important;
}

.current {
  border-bottom: 1px solid lightgray;
  padding-bottom: 1rem;
}

.subscription-plan {
  font-family: "proximanova-bold", sans-serif;
  font-size: 20px;
}

.subscription-price {
  font-family: "proximanova-regular", sans-serif;
  font-size: 20px;
}

.subscription-renewal {
  margin-top: -0.5rem;
  font-family: "proximanova-regular", sans-serif;
  font-size: 16px;
  white-space: nowrap;
}

.default {
  background-color: #e9ecef;
  border-radius: 0.25rem;
  padding-left: 5px;
  padding-right: 5px;
  font-size: 14px;
}

.edit-dropdown {
  background-color: transparent !important;
}
</style>
